// Nederlandse teksten voor het source-deel. Dezelfde sleutels als source.en.ts;
// de typecheck faalt als er een ontbreekt of bijkomt.

import type { Message, Vars } from './en.ts';
import type { sourceEn } from './source.en.ts';

const n = (vars: Vars, name = 'count'): number => Number(vars[name] ?? 0);

export const sourceNl: Readonly<Record<keyof typeof sourceEn, Message>> = {
  // --- SourceViewer: de bron openen -------------------------------------------
  'source.opening': 'De opgeslagen bron wordt geopend…',
  'source.tryAgain': 'Opnieuw proberen',
  'source.noVersion': 'Voor dit document is geen bronversie opgeslagen.',
  'source.couldNotOpen': 'De opgeslagen bron kon niet worden geopend.',
  'source.noStored': 'Voor deze versie is geen brondocument opgeslagen.',
  'source.noStoredDetail':
    'Dit document is vastgelegd zonder het bestand zelf, dus er is niets te tonen. De gegevens en de geschiedenis staan hieronder.',
  'source.unreachable': 'De dienst was niet bereikbaar.',
  'source.unreachableDetail': 'Over de bron is nu niets bekend, en dat is iets anders dan dat er niets is.',
  'source.absent': 'Het brondocument kon niet worden geopend.',
  'source.absentDetail':
    'Het is niet meer beschikbaar voor je sessie, of deze omgeving geeft geen URL’s voor de viewer uit.',

  // --- SourceViewer: de pagina -------------------------------------------------
  'source.openFullSize': 'De bron op ware grootte openen',
  'source.zoomHint': 'Klik om op ware grootte te openen',
  'source.fullSize': 'De bron, op ware grootte',
  'source.close': 'Sluiten',
  'source.imageAlt': 'Het opgeslagen brondocument',
  'source.preparing': 'De bron wordt klaargezet…',
  'source.unrenderable': (v: Vars) =>
    `Deze bron is ${v['mediaType'] === '' ? 'van een onbekend type' : `van het type ${String(v['mediaType'])}`}, en dat kan dit scherm niet tonen. Het bestand is opgeslagen en ongewijzigd; niets hier vervangt het zelf bekijken.`,

  // --- SourceViewer: het onderschrift onder de pagina ---------------------------
  'source.legend.version': 'Versie {version} · {mediaType} · {bytes} bytes',
  'source.legend.pages': (v: Vars) => `${n(v)} ${n(v) === 1 ? 'pagina' : 'pagina’s'}`,
  'source.legend.scanning': 'Je AI leest deze pagina nu; wat het vindt verschijnt ernaast.',
  'source.legend.notExtracted': 'Er is nog niets uit dit document gehaald, dus er is niets gemarkeerd.',
  'source.legend.highlighted': 'De gemarkeerde stukken zijn het bewijs dat bij het uitlezen per veld is vastgelegd.',
  'source.legend.otherVersion':
    'De velden hieronder zijn uit een andere bronversie gelezen, dus hier is niets gemarkeerd.',
  'source.legend.ocrBoxes':
    'De gemarkeerde stukken zijn waar OCR de tekst van elk veld las, precies zoals geciteerd. Een veld waarvan de tekst niet precies één keer is gevonden, staat er zonder plek bij.',
  'source.legend.pdfLocated':
    'De gemarkeerde tekst is het vastgelegde bewijs, teruggevonden op de getoonde pagina. Een veld waarvan het bewijs niet precies één keer is gevonden, staat er zonder plek bij.',
  'source.legend.pdfNoExcerpt': 'Bij geen enkel veld is een fragment als bewijs vastgelegd, dus op de pagina is niets gemarkeerd.',
  'source.legend.pdfNoneFound':
    'Geen van de vastgelegde fragmenten is precies één keer op de getoonde pagina gevonden, dus er is niets gemarkeerd.',
  'source.legend.imageNoPlace':
    'Bij geen enkel veld van deze {mediaType}-bron is een plek op de afbeelding vastgelegd, dus de velden staan er zonder plek bij in plaats van met een gok.',

  // --- SourceViewer: de veldenlijst --------------------------------------------
  'source.heading.page': 'Wat je AI op deze pagina las',
  'source.heading.document': 'Wat je AI in dit document las',
  'source.reading': 'Bezig met lezen…',
  'source.nothingRead': 'Er is nog niets uit gelezen.',
  'source.noFields': 'Bij deze lezing zijn geen velden vastgelegd.',
  'source.moreFields': (v: Vars) => `${n(v)} ${n(v) === 1 ? 'veld' : 'velden'} meer gelezen`,
  'source.confidence.none': 'Zekerheid voor geen enkel veld gemeten.',
  'source.confidence.all': 'De zekerheid staat bij elk veld, zoals gemeten.',
  'source.confidence.some': 'Zekerheid niet gemeten voor {unmeasured} van de {total} velden; bij de andere staat ze zoals gemeten.',
  'source.confidence.value': 'zekerheid {percent}',
  'source.derived': '{how} — het model gaf het niet op',
  'source.derived.amount_markers': 'afgeleid uit de bedragmarkeringen',
  'source.derived.vat_breakdown': 'afgeleid uit de btw-specificatie',
  'source.derived.arithmetic': 'afgeleid: de regels en btw-regels op de pagina tellen ertoe op',
  'source.derived.person': 'bevestigd door een mens: verbeterd in de briefpositie, passend bij een bestaande relatie',
  'source.noPlace': 'Geen plek op de pagina vastgelegd',
  'source.found': 'gevonden',
  'source.foundOnPage': 'gevonden op pagina {page}',
  'source.spanNotShown': 'Plek vastgelegd, hier niet getoond',
  'source.pageNotShown': 'Pagina {page}, hier niet getoond',
  'source.pdf.noText': 'Plek vastgelegd zonder de tekst, hier niet getoond',
  'source.pdf.looking': 'Zoeken op de pagina…',
  'source.pdf.ambiguous': 'Staat meer dan eens op de pagina, dus niet gemarkeerd',
  'source.pdf.notFound': 'Niet gevonden op de getoonde pagina, dus niet gemarkeerd',

  // --- Veldnamen (per vaste veldnaam) ------------------------------------------
  'source.field.documentType': 'Soort document',
  'source.field.supplierName': 'Leverancier',
  'source.field.supplierVatNumber': 'Btw-nummer',
  'source.field.supplierRegistrationNumber': 'KvK-nummer',
  'source.field.invoiceNumber': 'Factuurnummer',
  'source.field.invoiceDate': 'Factuurdatum',
  'source.field.dueDate': 'Vervaldatum',
  'source.field.currency': 'Valuta',
  'source.field.paymentMethod': 'Betaald met',
  'source.field.originalInvoiceNumber': 'Corrigeert factuur',
  'source.field.totalNet': 'Netto',
  'source.field.totalTax': 'Btw',
  'source.field.total': 'Totaal',
  'source.field.line': 'Regel {n} {part}',
  'source.field.vatRow': 'Btw-regel {n} {part}',
  'source.line.description': 'omschrijving',
  'source.line.quantity': 'aantal',
  'source.line.unitPrice': 'stukprijs',
  'source.line.net': 'netto',
  'source.line.taxRate': 'btw-tarief',

  // --- PdfView -----------------------------------------------------------------
  'source.pdf.rendering': 'De opgeslagen pdf wordt weergegeven…',
  'source.pdf.failed':
    'Deze pdf kon hier niet worden weergegeven. Hij is opgeslagen en ongewijzigd; niets op dit scherm vervangt het zelf bekijken.',
  'source.pdf.pageLimit': 'De eerste {shown} van {pages} pagina’s worden getoond.',

  // --- SupplierSuggestion ------------------------------------------------------
  'source.suggest.matchedOn.vat_number': 'btw-nummer',
  'source.suggest.matchedOn.registration_number': 'KvK-nummer',
  'source.suggest.noneTitle': 'Geen passende relatie in het boekhoudpakket',
  'source.suggest.noneBody':
    'Geen relatie daar heeft het btw- of KvK-nummer van deze leverancier. Maak de relatie aan in het boekhoudpakket en druk dan bij Koppelingen op “Check now”; de suggestie verschijnt hier zodra die is ingelezen.',
  'source.suggest.ambiguousTitle': (v: Vars) =>
    `${n(v)} relaties hebben dit ${String(v['matchedOn'])}`,
  'source.suggest.ambiguousBody':
    'Welke relatie deze leverancier is, valt niet uit het nummer af te leiden, dus er wordt niets voorgesteld. Een mens beslist dat bij de boekingsregels.',
  'source.suggest.title': 'Voorgestelde leveranciersregel',
  'source.suggest.badge': 'Suggestie · niet toegepast',
  'source.suggest.relationBefore': 'Deze leverancier is relatie ',
  'source.suggest.relationAfter': ' in het boekhoudpakket: hetzelfde {matchedOn} ({identifier}).',
  'source.suggest.costLedger': 'Kostenrekening',
  'source.suggest.chooseLedger': 'Kies een grootboekrekening…',
  'source.suggest.usedBefore': 'Eerder gebruikt voor deze relatie',
  'source.suggest.ledgerUse': '{ledger} — {invoices} van {read} eerdere facturen',
  'source.suggest.chart': 'Het rekeningschema',
  'source.suggest.noHistory':
    'Er is nog geen eerdere inkoopfactuur van deze relatie uit het boekhoudpakket ingelezen, dus er wordt geen grootboekrekening voorgesteld.',
  'source.suggest.noneInChart': (v: Vars) =>
    `${n(v, 'read') === 1 ? 'De ene eerdere factuur' : `Geen van de ${n(v, 'read')} eerdere facturen`} van deze relatie ${n(v, 'read') === 1 ? 'gebruikte geen' : 'gebruikte een'} grootboekrekening uit het huidige rekeningschema, dus er wordt geen grootboekrekening voorgesteld.`,
  'source.suggest.notUsed': (v: Vars) =>
    `Deze grootboekrekening is niet gebruikt op ${n(v, 'read') === 1 ? 'de ene ingelezen eerdere factuur' : `de ${n(v, 'read')} ingelezen eerdere facturen`} van deze relatie.`,
  'source.suggest.usedOn': (v: Vars) =>
    `Gebruikt op ${n(v, 'invoices')} van ${n(v, 'read') === 1 ? 'de ene ingelezen eerdere factuur' : `de ${n(v, 'read')} ingelezen eerdere facturen`} van deze relatie.`,
  'source.suggest.nonDeductible':
    'De btw op facturen van deze leverancier is niet aftrekbaar (bijv. horeca): boek het brutobedrag en vraag geen btw terug',
  'source.suggest.use': 'Deze regel gebruiken',
  'source.suggest.useNote': 'Slaat de regel op voor dit {matchedOn} en leest het document opnieuw. Er wordt niets geboekt.',
};
